import {
  getDefFixedBuffValue,
  getMDefFixedBuffValue,
  isDefFixedBuff,
  isMDefFixedBuff,
} from '@common/FabulaUltimaGame';

export type FuLocale = "zh" | "en";

export type FuBuffPresentation = {
  label: string;
  isMechanical: boolean;
};

export type FuBuffTemplate = {
  protocolPrefix: 'defFixed' | 'mDefFixed';
  inputPrefix: string;
  suggestion: string;
};

const text = {
  zh: {
    appTitle: "最终物语·战斗模拟器", localeToggle: "中文 | EN",
    statusPanel: "生物状态", add: "添加", buffs: "增益 / 减益", notStudied: "未研究",
    sent: "已发送：", historyLockedGm: "请先回到时间线末端再编辑。",
    historyLockedPlayer: "GM 正在查看历史状态，暂时无法编辑。",
    deleteCreature: "删除生物", buffHistoryHeading: "曾用过",
  },
  en: {
    appTitle: "Fabula Ultima Battle Simulator", localeToggle: "中文 | EN",
    statusPanel: "Creature status", add: "Add", buffs: "Buffs / debuffs", notStudied: "Unstudied",
    sent: "Sent: ", historyLockedGm: "Return to the timeline end before editing.",
    historyLockedPlayer: "The GM is viewing history; editing is temporarily unavailable.",
    deleteCreature: "Remove creature", buffHistoryHeading: "Recently used",
  },
} as const;

export function fuText(locale: FuLocale, key: keyof typeof text.zh): string {
  return text[locale][key];
}

export const statusLabels: Record<FuLocale, Record<string, string>> = {
  zh: { dazed: "眩晕", enraged: "激怒", poisoned: "中毒", slow: "迟缓", weak: "虚弱", shaken: "动摇" },
  en: { dazed: "Dazed", enraged: "Enraged", poisoned: "Poisoned", slow: "Slow", weak: "Weak", shaken: "Shaken" },
};

const buffTemplates: Record<FuLocale, FuBuffTemplate[]> = {
  zh: [
    { protocolPrefix: 'defFixed', inputPrefix: '物防固定', suggestion: '物防固定（输入数值）' },
    { protocolPrefix: 'mDefFixed', inputPrefix: '魔防固定', suggestion: '魔防固定（输入数值）' },
  ],
  en: [
    { protocolPrefix: 'defFixed', inputPrefix: 'Fixed Defense', suggestion: 'Fixed Defense (enter value)' },
    { protocolPrefix: 'mDefFixed', inputPrefix: 'Fixed Magic Defense', suggestion: 'Fixed Magic Defense (enter value)' },
  ],
};

export function getFuBuffTemplates(locale: FuLocale): FuBuffTemplate[] {
  return buffTemplates[locale];
}

export function formatFuBuff(buff: string, locale: FuLocale): FuBuffPresentation {
  if (isDefFixedBuff(buff)) {
    const value = getDefFixedBuffValue(buff);
    if (Number.isFinite(value)) {
      return {
        label: locale === 'zh' ? `物理防御固定 ${value}` : `Fixed Defense ${value}`,
        isMechanical: true,
      };
    }
  }
  if (isMDefFixedBuff(buff)) {
    const value = getMDefFixedBuffValue(buff);
    if (Number.isFinite(value)) {
      return {
        label: locale === 'zh' ? `魔法防御固定 ${value}` : `Fixed Magic Defense ${value}`,
        isMechanical: true,
      };
    }
  }
  return { label: buff, isMechanical: false };
}

export function resolveFuBuffInput(input: string, locale: FuLocale): string | null {
  const trimmed = input.trim();
  if (!trimmed || /[`\r\n]/.test(input)) return null;

  for (const template of buffTemplates[locale]) {
    if (trimmed === template.inputPrefix || trimmed === template.protocolPrefix) return null;
    const localizedMatch = trimmed.match(new RegExp(`^${template.inputPrefix}\\s+(-?\\d+)$`, 'i'));
    if (localizedMatch) return `${template.protocolPrefix}${localizedMatch[1]}`;
  }
  return trimmed;
}
